import React, { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Volume, Volume1, Volume2, VolumeX } from 'lucide-react';

/**
 * VolumeControl
 * -----------------------------------------------------------------------------
 * Renders the existing 40x40 mute/unmute button (look & click behavior preserved)
 * and adds a hover-activated vertical volume slider above it.
 *
 * - Click on the icon button → calls `onToggleMute` (parent owns mute state)
 * - Hovering the button OR slider area → reveals the vertical slider above
 * - Dragging the slider sets `videoRef.current.volume` (0 → 1)
 * - If the user adjusts the slider while muted, the video is auto-unmuted so
 *   the change is audible (mute click behavior itself is untouched)
 * - Icon reflects current state:  muted/0 → VolumeX,
 *   low → Volume, mid → Volume1, high → Volume2
 *
 * Theme: dark glass background, champagne gold (#D4AF37) active track + handle.
 */
const GOLD = '#D4AF37';
const TRACK_HEIGHT = 110; // px — interactive rail height

const VolumeControl = ({
  videoRef,
  isMuted,
  onToggleMute,
  initialVolume = 1,
  buttonClassName = 'w-10 h-10 rounded-full bg-black/50 backdrop-blur-sm hover:bg-black/70 flex items-center justify-center transition-all duration-200 group',
  iconClassName = 'w-5 h-5 text-white group-hover:scale-110 transition-transform',
  motionDelay = 0.5,
  ariaLabelMuted = 'Unmute video',
  ariaLabelUnmuted = 'Mute video',
  testId,
}) => {
  const [volume, setVolume] = useState(initialVolume);
  const [hovered, setHovered] = useState(false);
  const [dragging, setDragging] = useState(false);
  const trackRef = useRef(null);
  const hideTimerRef = useRef(null);

  // Keep underlying <video> element volume in sync
  useEffect(() => {
    if (videoRef && videoRef.current) {
      videoRef.current.volume = volume;
    }
  }, [volume, videoRef]);

  // Pick the appropriate Lucide icon based on muted/volume state
  const renderIcon = () => {
    if (isMuted || volume === 0) {
      return <VolumeX className={iconClassName} />;
    }
    if (volume <= 0.33) return <Volume className={iconClassName} />;
    if (volume <= 0.66) return <Volume1 className={iconClassName} />;
    return <Volume2 className={iconClassName} />;
  };

  // ---- Hover handling (button + slider together) ---------------------------
  const showSlider = useCallback(() => {
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
    setHovered(true);
  }, []);

  const scheduleHide = useCallback(() => {
    // Slight delay so users can move the mouse from button → slider without losing it
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    hideTimerRef.current = setTimeout(() => {
      if (!dragging) setHovered(false);
    }, 140);
  }, [dragging]);

  // If user is dragging, keep the slider visible regardless of pointer position
  useEffect(() => {
    if (!dragging) return;
    const handlePointerMove = (e) => updateFromClientY(e.clientY);
    const handlePointerUp = () => {
      setDragging(false);
      // After releasing, if pointer is outside the area, allow auto-hide
      scheduleHide();
    };
    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
    window.addEventListener('pointercancel', handlePointerUp);
    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
      window.removeEventListener('pointercancel', handlePointerUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dragging]);

  useEffect(() => {
    return () => {
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    };
  }, []);

  // ---- Slider math ----------------------------------------------------------
  const updateFromClientY = (clientY) => {
    const rail = trackRef.current;
    if (!rail) return;
    const rect = rail.getBoundingClientRect();
    // 0 at the bottom, 1 at the top
    const raw = 1 - (clientY - rect.top) / rect.height;
    const clamped = Math.max(0, Math.min(1, raw));
    setVolume(clamped);

    // If user is actively adjusting volume while muted, auto-unmute so it is
    // audible (the mute *button* click behavior is unchanged elsewhere).
    if (isMuted && clamped > 0 && typeof onToggleMute === 'function') {
      onToggleMute();
    }
  };

  const handleTrackPointerDown = (e) => {
    e.preventDefault();
    setDragging(true);
    updateFromClientY(e.clientY);
  };

  const displayVolume = isMuted ? 0 : volume;
  const fillPct = `${displayVolume * 100}%`;

  return (
    <div
      className="relative"
      onMouseEnter={showSlider}
      onMouseLeave={scheduleHide}
      data-testid={testId ? `${testId}-wrapper` : undefined}
    >
      {/* Vertical Volume Slider — appears on hover ABOVE the button */}
      <AnimatePresence>
        {hovered && (
          <motion.div
            initial={{ opacity: 0, y: 6, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 6, scale: 0.96 }}
            transition={{ duration: 0.18, ease: 'easeOut' }}
            className="absolute left-1/2 -translate-x-1/2 z-30"
            style={{ bottom: 'calc(100% + 10px)' }}
            onMouseEnter={showSlider}
            onMouseLeave={scheduleHide}
            data-testid={testId ? `${testId}-slider` : undefined}
          >
            {/* Glass panel */}
            <div
              className="flex flex-col items-center justify-between rounded-full backdrop-blur-md"
              style={{
                width: 36,
                height: TRACK_HEIGHT + 36,
                paddingTop: 12,
                paddingBottom: 12,
                background: 'rgba(15, 15, 17, 0.78)',
                border: '1px solid rgba(212, 175, 55, 0.35)',
                boxShadow: '0 10px 30px rgba(0,0,0,0.45), 0 0 0 1px rgba(255,255,255,0.04) inset',
              }}
            >
              {/* Volume % readout */}
              <div
                className="text-[10px] tracking-wider tabular-nums select-none"
                style={{ color: GOLD, fontFamily: 'inherit' }}
              >
                {Math.round(displayVolume * 100)}
              </div>

              {/* Track (interactive rail) */}
              <div
                ref={trackRef}
                onPointerDown={handleTrackPointerDown}
                className="relative cursor-pointer"
                style={{
                  width: 4,
                  height: TRACK_HEIGHT,
                  borderRadius: 999,
                  background: 'rgba(255, 255, 255, 0.18)',
                  touchAction: 'none',
                }}
              >
                {/* Active (gold) fill */}
                <div
                  className="absolute left-0 right-0 bottom-0 rounded-full pointer-events-none"
                  style={{
                    height: fillPct,
                    background: `linear-gradient(to top, ${GOLD}, #E8C766)`,
                    boxShadow: `0 0 8px ${GOLD}66`,
                    transition: dragging ? 'none' : 'height 120ms ease-out',
                  }}
                />
                {/* Handle */}
                <div
                  className="absolute -translate-x-1/2 -translate-y-1/2 rounded-full pointer-events-none"
                  style={{
                    left: '50%',
                    bottom: fillPct,
                    width: 12,
                    height: 12,
                    background: GOLD,
                    border: '2px solid #1a1a1c',
                    boxShadow: `0 0 0 1px ${GOLD}, 0 2px 6px rgba(0,0,0,0.5)`,
                    transition: dragging ? 'none' : 'bottom 120ms ease-out',
                  }}
                />
              </div>

              {/* Mute indicator dot at the bottom of the rail */}
              <div
                className="rounded-full"
                style={{
                  width: 4,
                  height: 4,
                  background: displayVolume === 0 ? GOLD : 'rgba(255,255,255,0.35)',
                }}
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* The existing Mute/Unmute Button — behavior untouched */}
      <motion.button
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: motionDelay }}
        onClick={onToggleMute}
        className={buttonClassName}
        aria-label={isMuted ? ariaLabelMuted : ariaLabelUnmuted}
        data-testid={testId}
      >
        {renderIcon()}
      </motion.button>
    </div>
  );
};

export default VolumeControl;
